import { KeyDict } from '@/types'
import store from '@/store'
import { isEqual } from 'lodash'

export enum VideoPlayerPlayState {
  LOADING = 'loading',
  PLAYING = 'playing',
  PAUSED = 'paused',
  READY = 'ready'
}

export class VideoPlayerLastClicked {
  stopTime: number = 0
  pauseTime: number = 0
  playTime: number = 0
}

export class VideoPlayerLocalState {
  playstate: VideoPlayerPlayState = VideoPlayerPlayState.LOADING
  playhead: number = 0
}

export class VideoPlayerActualState {
  videos: KeyDict<boolean> = {}
  video: string | null = null
  playhead: number = 0
  playing: boolean = false
  paused: boolean = false

  fromJSON (data) {
    if (data.videos) {
      const videos: KeyDict<boolean> = {}
      for (const url in data.videos)
        videos[url] = data.videos[url]
      this.videos = videos
    }
    if (data.video)
      this.video = data.video
    if (data.playhead)
      this.playhead = data.playhead
    if (data.playing)
      this.playing = data.playing
    if (data.playing)
      this.paused = data.paused
  }
}

export class VideoPlayerClipProperties {
  cueIn: number = 0
}

export class VideoPlayerDesiredState {
  video: string = ""
  timestamp: number = 0
  clipProperties: KeyDict<VideoPlayerClipProperties> = {}

  fromJSON (data) {
    if (data.videos !== undefined) {
      const videos: KeyDict<boolean> = {}
      for (const url in data.videos)
        videos[url] = data.videos[url]
    }
    if (data.video !== undefined)
      this.video = data.video
    if (data.timestamp !== undefined)
      this.timestamp = data.timestamp
    if (data.clipProperties) {
      this.clipProperties = data.clipProperties
    }
  }

  toJSON () {
    return {
      'video': this.video,
      'timestamp': this.timestamp
    }
  }
}

export class VideoPlayer {
  actual_states: KeyDict<VideoPlayerActualState> = {}
  actual_playhead: KeyDict<number> = {}
  desired_state: VideoPlayerDesiredState = new VideoPlayerDesiredState()
  id: string = ''

  constructor (id: string) {
    this.id = id
  }

  toJSON () {
    return {
      actual_states: {},
      desired_state: this.desired_state.toJSON()
    }
  }

  fromJSON (data: any) {
    if (data.desired_state)
      this.desired_state.fromJSON(data.desired_state)

    if (data.actual_states) {
      const actual_states: KeyDict<VideoPlayerActualState> = {}
      for (const machinename in data.actual_states) {
        actual_states[machinename] = new VideoPlayerActualState()
        actual_states[machinename].fromJSON(data.actual_states[machinename])
      }
      if(!isEqual(this.actual_states, actual_states)) {
        this.actual_states = actual_states
      }
    }

    if(data.actual_playhead) {
      let actual_playhead = 0
      for (const machinename in data.actual_playhead) {
        if (machinename && machinename in this.actual_states) {
          for (const videoname in data.actual_playhead[machinename]) {
            if(videoname === this.actual_states[machinename].video) {
              actual_playhead = data.actual_playhead[machinename][videoname]
            }
          }
        }
        this.actual_playhead[machinename] = actual_playhead
      }
    }
  }

  loadedAssets (): string [] {
    const assetList: string[] = []
    for (const state of Object.values(this.actual_states)) {
      for (const assetid of Object.keys(state.videos)) {
        if (!assetList.includes(assetid)) {
          assetList.push(assetid)
        }
      }
    }
    return assetList
  }

  getPlayhead (assetid: string): number {
    let playhead = -1
    if (this.actual_states) {
      for (const machine in this.actual_states) {
        if (this.actual_states[machine].video == assetid &&
            this.actual_states[machine].playing) {
          const ph = this.actual_playhead[machine]
          if (ph && (ph < playhead || playhead < 0))
            playhead = ph
        } else if (this.desired_state.clipProperties[assetid]) {
          playhead = this.desired_state.clipProperties[assetid].cueIn
        }
      }
    }
    if (playhead < 0)
      playhead = 0
    return playhead
  }

  getState (assetid: string): VideoPlayerPlayState {
    let lastClicked = new VideoPlayerLastClicked()
    if (store.state.videoPlayer.lastClicked[this.id] && store.state.videoPlayer.lastClicked[this.id][assetid]) {
      lastClicked = store.state.videoPlayer.lastClicked[this.id][assetid]
    }
    let lastChangeTime = lastClicked.stopTime
    const lastPlayTime = lastClicked.playTime
    const lastPauseTime = lastClicked.pauseTime
    let lastChangeState = VideoPlayerPlayState.LOADING
    if (lastPlayTime > lastChangeTime) {
      lastChangeTime = lastPlayTime
      lastChangeState = VideoPlayerPlayState.PLAYING
    }
    if (lastPauseTime > lastChangeTime) {
      lastChangeTime = lastPauseTime
      lastChangeState = VideoPlayerPlayState.PAUSED
    }

    if ((new Date()).getTime() - lastChangeTime < 1500) {
      return lastChangeState
    }

    let numberOfMachineReady : number = 0
    let numberOfMachinePlaying : number = 0
    let numberOfMachinePaused : number = 0
    if (this.actual_states) {
      for (const machine in this.actual_states) {
        if(assetid && this.actual_states[machine].videos[assetid]) {
          numberOfMachineReady += 1
        }
        if(this.actual_states[machine].video == assetid &&
            this.actual_states[machine].playing) {
          numberOfMachinePlaying += 1
          if (this.actual_states[machine].paused)
            numberOfMachinePaused += 1
        }
      }
    }
    if (numberOfMachinePaused > 1) {
      return VideoPlayerPlayState.PAUSED
    }
    if (numberOfMachinePlaying > 1) {
      return VideoPlayerPlayState.PLAYING
    }
    return numberOfMachineReady > 1 ? VideoPlayerPlayState.READY : VideoPlayerPlayState.LOADING
  }
}
